import { useState, useRef, useEffect } from 'react';
import { ArrowUp, ArrowDown } from 'lucide-react';
import { ActionButtonBase } from './ActionButtonBase';
import { useLanguage } from '../../contexts/LanguageContext';
import type { SortType } from '../../types/post';

export type { SortType };

interface SortSelectorProps {
  sortType: SortType;
  sortOrder: 'asc' | 'desc';
  onSortTypeChange: (sortType: SortType) => void;
  onSortOrderChange: (sortOrder: 'asc' | 'desc') => void;
}

export function SortSelector({ sortType, sortOrder, onSortTypeChange, onSortOrderChange }: SortSelectorProps) {
  const { t } = useLanguage();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const getSortLabel = (type: SortType) => {
    switch (type) {
      case 'channel':
        return t.posts.sortChannel;
      case 'date':
        return t.posts.sortDate;
      case 'alphabetical':
        return t.posts.sortAlphabetical;
    }
  };

  const handleSelect = (type: SortType) => {
    onSortTypeChange(type);
    setIsOpen(false);
  };

  const toggleSortOrder = () => {
    onSortOrderChange(sortOrder === 'asc' ? 'desc' : 'asc');
  };

  return (
    <div className="flex items-center gap-2">
      <div className="relative" ref={dropdownRef}>
        {/* Trigger button */}
        <ActionButtonBase
          label={`${t.posts.sortBy}: ${getSortLabel(sortType)}`}
          onClick={() => setIsOpen(!isOpen)}
          variant="neutral"
          expanded={true}
          revealLabelOnHover={false}
          className="h-9 px-4"
          title={t.posts.sortBy}
        />

        {/* Dropdown */}
        {isOpen && (
          <div 
            className="absolute top-full right-0 mt-2 w-48 backdrop-blur-md rounded-2xl shadow-xl z-50 py-2"
            style={{
              backgroundColor: 'rgba(var(--color-background-rgb), 0.9)',
              border: `1px solid var(--color-border)`
            }}
          >
            <button
              onClick={() => handleSelect('channel')}
              className="w-full px-4 py-2 text-left text-sm transition-colors"
              style={{
                backgroundColor: sortType === 'channel' ? 'var(--color-surface)' : 'transparent',
                color: sortType === 'channel' ? 'var(--color-primary)' : 'var(--color-text-primary)'
              }}
              onMouseEnter={(e) => {
                if (sortType !== 'channel') {
                  e.currentTarget.style.backgroundColor = 'var(--color-surface)';
                }
              }}
              onMouseLeave={(e) => {
                if (sortType !== 'channel') {
                  e.currentTarget.style.backgroundColor = 'transparent';
                }
              }}
            >
              {t.posts.sortByChannel}
            </button>
            <button
              onClick={() => handleSelect('date')}
              className="w-full px-4 py-2 text-left text-sm transition-colors"
              style={{
                backgroundColor: sortType === 'date' ? 'var(--color-surface)' : 'transparent',
                color: sortType === 'date' ? 'var(--color-primary)' : 'var(--color-text-primary)'
              }}
              onMouseEnter={(e) => {
                if (sortType !== 'date') {
                  e.currentTarget.style.backgroundColor = 'var(--color-surface)';
                }
              }}
              onMouseLeave={(e) => {
                if (sortType !== 'date') {
                  e.currentTarget.style.backgroundColor = 'transparent';
                }
              }}
            >
              {t.posts.sortByDate}
            </button>
            <button
              onClick={() => handleSelect('alphabetical')}
              className="w-full px-4 py-2 text-left text-sm transition-colors"
              style={{
                backgroundColor: sortType === 'alphabetical' ? 'var(--color-surface)' : 'transparent',
                color: sortType === 'alphabetical' ? 'var(--color-primary)' : 'var(--color-text-primary)'
              }}
              onMouseEnter={(e) => {
                if (sortType !== 'alphabetical') {
                  e.currentTarget.style.backgroundColor = 'var(--color-surface)';
                }
              }}
              onMouseLeave={(e) => {
                if (sortType !== 'alphabetical') {
                  e.currentTarget.style.backgroundColor = 'transparent';
                }
              }}
            >
              {t.posts.sortByAlphabetical}
            </button>
          </div>
        )}
      </div>

      {/* Sort order toggle button */}
      <ActionButtonBase
        icon={sortOrder === 'asc' ? ArrowUp : ArrowDown}
        onClick={toggleSortOrder}
        variant="neutral"
        className="h-9 w-9"
        revealLabelOnHover={false}
        title={sortOrder === 'asc' ? t.posts.sortAscending : t.posts.sortDescending}
      />
    </div>
  );
}